/**
 * The room a drop of pixel art may fill (DESIGN.md §9, "Pixel art", "Drops"),
 * measured off what is actually set in its surface: right of its words, left
 * of its controls, down to a named foot.
 */

/** Clear space kept between the art and the words or controls beside it. */
const CLEAR = 24;
/** Smaller than these it would be a sliver of cells. */
const LEAST_WIDE = 64;
const LEAST_TALL = 24;
/** Wider than these it stops being a touch and becomes a feature. */
const MOST = 320;
/** A Corner, each way: down a tall panel it otherwise filled a third of the screen. */
const CORNER_MOST = 200;
const EDGE_MOST = 160;

export interface RoomSpec {
  /** On the window's right edge beside the surface, rather than inside it. */
  readonly edge: boolean;
  /** Where an over-wide room keeps its art: at its right end, or in its middle. */
  readonly keep: "end" | "middle";
  /** The words the room starts right of. */
  readonly clear: string | undefined;
  /** The controls the room ends left of, where they sit beside it. */
  readonly until: string | undefined;
  /** The element whose foot the room ends at; the surface's foot without one. */
  readonly down: string | undefined;
}

/** In layout pixels from the surface's inner top-left, and its four rounds. */
export interface Room {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly round: string;
}

interface Box {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * How far right a thing reaches: a control its whole box, words the lines
 * set, and a thing with no words (a bar, a mark) its box.
 */
function rightOf(element: Element): number {
  if (element.matches("button, a, input, select, textarea")) {
    return element.getBoundingClientRect().right;
  }
  const range = document.createRange();
  range.selectNodeContents(element);
  const rights = [...range.getClientRects()].map((rect) => rect.right);
  return rights.length === 0
    ? element.getBoundingClientRect().right
    : Math.max(...rights);
}

function all(host: Element, selector: string | undefined): readonly Element[] {
  return selector === undefined ? [] : [...host.querySelectorAll(selector)];
}

function beside(rect: DOMRect, top: number, bottom: number): boolean {
  return rect.height > 0 && rect.bottom > top && rect.top < bottom;
}

/** The room inside the surface, in client pixels. */
function inside(host: HTMLElement, inner: Box, spec: RoomSpec, scale: number): Box {
  const clear = CLEAR * scale;
  const top = inner.top;
  const foot = spec.down === undefined ? null : host.querySelector(spec.down);
  const corner = spec.keep === "end";
  const most = (corner ? CORNER_MOST : MOST) * scale;
  const bottom = Math.min(
    foot === null
      ? inner.bottom
      : Math.min(inner.bottom, foot.getBoundingClientRect().bottom),
    corner ? top + most : Number.POSITIVE_INFINITY,
  );
  const left = Math.max(
    inner.left,
    ...all(host, spec.clear)
      .filter((element) => beside(element.getBoundingClientRect(), top, bottom))
      .map((element) => rightOf(element) + clear),
  );
  const right = Math.min(
    inner.right,
    ...all(host, spec.until)
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => beside(rect, top, bottom) && rect.left >= left - clear)
      .map((rect) => rect.left - clear),
  );
  const over = right - left - most;
  if (over <= 0) return { left, top, right, bottom };
  return spec.keep === "end"
    ? { left: left + over, top, right, bottom }
    : { left: left + over / 2, top, right: right - over / 2, bottom };
}

/** On the window's edge beside the surface: twice as tall as it is wide. */
function onEdge(host: HTMLElement, inner: Box, spec: RoomSpec): Box {
  const page = host.closest(".page") ?? document.documentElement;
  const right = page.getBoundingClientRect().right;
  const words = Math.max(inner.left, ...all(host, spec.clear).map(rightOf)) + CLEAR;
  const left = Math.max(words, right - EDGE_MOST);
  return { left, top: inner.top, right, bottom: inner.top + (right - left) * 2 };
}

/** A corner of the room that meets a corner of the surface takes its inner round. */
function roundOf(room: Box, inner: Box, style: CSSStyleDeclaration): string {
  const at = (a: number, b: number) => Math.abs(a - b) < 1.5;
  const radius = (value: string, border: string) =>
    Math.max(0, parseFloat(value) - parseFloat(border));
  const corners = [
    at(room.top, inner.top) && at(room.left, inner.left)
      ? radius(style.borderTopLeftRadius, style.borderTopWidth)
      : 0,
    at(room.top, inner.top) && at(room.right, inner.right)
      ? radius(style.borderTopRightRadius, style.borderTopWidth)
      : 0,
    at(room.bottom, inner.bottom) && at(room.right, inner.right)
      ? radius(style.borderBottomRightRadius, style.borderBottomWidth)
      : 0,
    at(room.bottom, inner.bottom) && at(room.left, inner.left)
      ? radius(style.borderBottomLeftRadius, style.borderBottomWidth)
      : 0,
  ];
  return corners.map((value) => `${String(value)}px`).join(" ");
}

/**
 * The room, or null where there is too little to draw in. A surface arriving
 * at a scale (a modal growing in) is measured as it will be laid out.
 */
export function measureRoom(host: HTMLElement, spec: RoomSpec): Room | null {
  const frame = host.getBoundingClientRect();
  const scale = host.offsetWidth > 0 ? frame.width / host.offsetWidth : 1;
  const style = getComputedStyle(host);
  const inner: Box = {
    left: frame.left + parseFloat(style.borderLeftWidth) * scale,
    top: frame.top + parseFloat(style.borderTopWidth) * scale,
    right: frame.right - parseFloat(style.borderRightWidth) * scale,
    bottom: frame.bottom - parseFloat(style.borderBottomWidth) * scale,
  };
  const room = spec.edge ? onEdge(host, inner, spec) : inside(host, inner, spec, scale);
  const width = Math.round((room.right - room.left) / scale);
  const height = Math.round((room.bottom - room.top) / scale);
  if (width < LEAST_WIDE || height < LEAST_TALL) return null;
  return {
    // A surface that scrolls (a wide table) carries the art with its content.
    left: Math.round((room.left - inner.left) / scale + host.scrollLeft),
    top: Math.round((room.top - inner.top) / scale + host.scrollTop),
    width,
    height,
    round: roundOf(room, inner, style),
  };
}
